import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../lib/api';
import { Mail, Lock, Eye, EyeOff, User, AlertCircle } from 'lucide-react';

export default function Register() {
  const { registerAgent } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    vehicleType: 'motorcycle',
    vehiclePlate: '',
    vehicleColor: '',
    licenseNumber: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsLoading(true);
    try {
      await registerAgent({
        email: formData.email,
        phone: formData.phone,
        fullName: formData.fullName,
        password: formData.password,
        vehicleType: formData.vehicleType as any,
        vehiclePlate: formData.vehiclePlate || undefined,
        vehicleColor: formData.vehicleColor || undefined,
        licenseNumber: formData.licenseNumber || undefined,
      });
      navigate('/');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center p-4 bg-background pt-8 pb-4">
      <div className="w-full max-w-md">
        <form onSubmit={handleSubmit} className="card space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="fullName" className="input-label">Nom complet</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
              <input
                id="fullName"
                type="text"
                className="input-field pl-10"
                value={formData.fullName}
                onChange={e => setFormData({...formData, fullName: e.target.value})}
                placeholder="Votre nom complet"
                required
                autoComplete="name"
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="email" className="input-label">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
              <input
                id="email"
                type="email"
                className="input-field pl-10"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="votre@email.com"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="phone" className="input-label">Téléphone</label>
            <input
              id="phone"
              type="tel"
              className="input-field"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
              placeholder="+226 XX XX XX XX"
              required
              autoComplete="tel"
            />
          </div>

          <div className="input-group">
            <label htmlFor="password" className="input-label">Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="input-field pl-10 pr-10"
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
                placeholder="•••••••• (min 8 caractères)"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword" className="input-label">Confirmer le mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                className="input-field pl-10"
                value={formData.confirmPassword}
                onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="border-t border-border pt-4 mt-4 space-y-4">
            <h3 className="font-semibold text-text">Informations véhicule</h3>
            <div className="input-group">
              <label className="input-label">Type de véhicule</label>
              <select
                className="input-field"
                value={formData.vehicleType}
                onChange={e => setFormData({...formData, vehicleType: e.target.value})}
              >
                <option value="motorcycle">Moto</option>
                <option value="bicycle">Vélo</option>
                <option value="car">Voiture</option>
                <option value="tricycle">Tricycle</option>
                <option value="foot">À pied</option>
              </select>
            </div>
            <div className="input-group">
              <label htmlFor="vehiclePlate" className="input-label">Plaque d'immatriculation (optionnel)</label>
              <input
                id="vehiclePlate"
                type="text"
                className="input-field"
                value={formData.vehiclePlate}
                onChange={e => setFormData({...formData, vehiclePlate: e.target.value})}
                placeholder="BF-1234-AB"
              />
            </div>
            <div className="input-group">
              <label htmlFor="vehicleColor" className="input-label">Couleur du véhicule (optionnel)</label>
              <input
                id="vehicleColor"
                type="text"
                className="input-field"
                value={formData.vehicleColor}
                onChange={e => setFormData({...formData, vehicleColor: e.target.value})}
                placeholder="Noir, Blanc, Rouge..."
              />
            </div>
            <div className="input-group">
              <label htmlFor="licenseNumber" className="input-label">Numéro de permis (optionnel)</label>
              <input
                id="licenseNumber"
                type="text"
                className="input-field"
                value={formData.licenseNumber}
                onChange={e => setFormData({...formData, licenseNumber: e.target.value})}
                placeholder="PERMIS-12345"
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
            {isLoading ? 'Inscription...' : 'Créer mon compte'}
          </button>
        </form>

        <p className="text-center text-text-secondary mt-6">
          Déjà un compte ? <Link to="/login" className="text-primary font-medium">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}